import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { marketingManifestApi, asyncTasksListing, asyncTaskResult, asyncTaskCancel } from "@/network/network";
import { showToast } from "@/other/utils/utils";
import { ASYNC_TASK_TYPES, ASYNC_TASK_STATUS } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";

export interface HistoryEntry {
  id: number;
  created_at: string;
  [key: string]: any;
}

function normalizeHistoryList(data: unknown): HistoryEntry[] {
  const list = (data as any)?.results ?? (Array.isArray(data) ? data : []);
  return (list as any[])
    .map((e) => ({ ...e, id: e.history_id, created_at: e.history_date }))
    .filter((e) => Number.isFinite(e.id));
}

export interface GeneratedTag {
  label: string;
  type: string;
  tag_group: string;
  description: string;
}

export interface GeneratedMapping {
  problem_label: string;
  solution_label: string;
}

interface SMMGeneratorContextValue {
  manifestContent: string;
  confidenceScores: Record<string, any> | null;

  isLoading: boolean;
  isGenerating: boolean;

  history: HistoryEntry[];
  isLoadingHistory: boolean;

  previewEntry: HistoryEntry | null;
  previewContent: string | null;
  previewConfidenceScores: Record<string, any> | null;
  isLoadingPreview: boolean;
  handlePreviewHistory: (historyId: number) => Promise<void>;
  clearHistoryPreview: () => void;

  isGeneratingTags: boolean;
  isSavingTags: boolean;
  tagReviewOpen: boolean;
  setTagReviewOpen: (open: boolean) => void;
  generatedTags: GeneratedTag[];
  generatedMappings: GeneratedMapping[];

  hasOpenAIKey: boolean;

  cancelGeneration: () => Promise<void>;
  handleGenerateTags: () => Promise<void>;
  cancelGenerateTags: () => Promise<void>;
  handleSaveTags: (tags: GeneratedTag[], mappings: GeneratedMapping[]) => Promise<void>;
}

const SMMGeneratorContext = createContext<SMMGeneratorContextValue | undefined>(undefined);

export const SMMGeneratorProvider = ({ children }: { children: ReactNode }) => {
  const { selectedBoard, project, userPreferences, editorHandler } = useSwivuBryntumContext();
  const slug = (selectedBoard?.record as any)?.slug as string | undefined;
  const boardId = (selectedBoard?.record as any)?.id as number | undefined;
  const api = useMemo(() => (slug ? marketingManifestApi(slug) : null), [slug]);

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey,
  });

  const hasOpenAIKey = userPreferences
    ? userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true"
    : false;

  const [manifestContent, setManifestContent] = useState("");
  const [confidenceScores, setConfidenceScores] = useState<Record<string, any> | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [previewEntry, setPreviewEntry] = useState<HistoryEntry | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewConfidenceScores, setPreviewConfidenceScores] = useState<Record<string, any> | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [tagReviewOpen, setTagReviewOpen] = useState(false);
  const [generatedTags, setGeneratedTags] = useState<GeneratedTag[]>([]);
  const [generatedMappings, setGeneratedMappings] = useState<GeneratedMapping[]>([]);

  const manifestTaskIdRef = useRef<number | null>(null);
  const tagsTaskIdRef = useRef<number | null>(null);
  const processedTaskIdsRef = useRef<Set<number>>(new Set());

  const clearHistoryPreview = useCallback(() => {
    setPreviewEntry(null);
    setPreviewContent(null);
    setPreviewConfidenceScores(null);
  }, []);

  // Reset transient state when the active board changes.
  useEffect(() => {
    processedTaskIdsRef.current.clear();
    manifestTaskIdRef.current = null;
    tagsTaskIdRef.current = null;
    setPreviewEntry(null);
    setPreviewContent(null);
    setPreviewConfidenceScores(null);
    setIsGenerating(false);
    setIsGeneratingTags(false);
  }, [slug, boardId]);

  const loadManifest = useCallback(async (opts?: { silent?: boolean }) => {
    if (!api) {
      setManifestContent("");
      setConfidenceScores(null);
      setIsLoading(false);
      return;
    }
    if (!opts?.silent) setIsLoading(true);
    try {
      const response = await api.get();
      const data = response.data;
      if (data?.detail != null || data?.content == null) {
        setManifestContent("");
        setConfidenceScores(null);
        return;
      }
      setManifestContent(data.content || "");
      setConfidenceScores(data.confidence_scores || null);
    } catch {
      setManifestContent("");
      setConfidenceScores(null);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const loadHistory = useCallback(async () => {
    if (!api) return;
    setIsLoadingHistory(true);
    try {
      const response = await api.getHistory();
      setHistory(normalizeHistoryList(response.data));
    } catch {
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [api]);

  useEffect(() => {
    loadManifest();
  }, [loadManifest]);

  useEffect(() => {
    if (manifestContent) {
      loadHistory();
    } else {
      setHistory([]);
    }
  }, [manifestContent, loadHistory]);

  useEffect(() => {
    if (!api || !boardId) return;

    let isMounted = true;

    const pollAsyncTasks = async () => {
      try {
        const response = await asyncTasksListing();
        if (!isMounted) return;

        const allTasks: any[] = response.data || [];
        const boardTasks = allTasks
          .filter((task: any) => task.meta_data?.board_id === boardId)
          .sort((a: any, b: any) => b.id - a.id);

        const manifestTask = boardTasks.find(
          (task: any) => task.type === ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION && !task.is_retrieved,
        );
        const tagsTask = boardTasks.find(
          (task: any) => task.type === ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION && !task.is_retrieved,
        );

        if (manifestTask) {
          if (manifestTask.status === ASYNC_TASK_STATUS.PENDING) {
            manifestTaskIdRef.current = manifestTask.id;
            setIsGenerating(true);
          } else if (
            manifestTask.status === ASYNC_TASK_STATUS.COMPLETED &&
            !processedTaskIdsRef.current.has(manifestTask.id)
          ) {
            processedTaskIdsRef.current.add(manifestTask.id);
            manifestTaskIdRef.current = null;
            try {
              await loadManifest({ silent: true });
            } catch {
              showToast({ msg: "Failed to retrieve manifest generation result.", type: "error", duration: 3000 });
            } finally {
              if (isMounted) setIsGenerating(false);
            }
          } else if (
            manifestTask.status === ASYNC_TASK_STATUS.CANCELLED ||
            manifestTask.status === ASYNC_TASK_STATUS.FAILED
          ) {
            if (manifestTaskIdRef.current === manifestTask.id) {
              manifestTaskIdRef.current = null;
              setIsGenerating(false);
              if (manifestTask.status === ASYNC_TASK_STATUS.FAILED) {
                showToast({ msg: "Manifest generation failed.", type: "error", duration: 3000 });
              }
            }
          }
        }

        if (tagsTask) {
          if (tagsTask.status === ASYNC_TASK_STATUS.PENDING) {
            tagsTaskIdRef.current = tagsTask.id;
            setIsGeneratingTags(true);
          } else if (
            tagsTask.status === ASYNC_TASK_STATUS.COMPLETED &&
            !processedTaskIdsRef.current.has(tagsTask.id)
          ) {
            processedTaskIdsRef.current.add(tagsTask.id);
            tagsTaskIdRef.current = null;
            try {
              const resultResponse = await asyncTaskResult(tagsTask.id);
              if (!isMounted) return;
              const data = resultResponse.data;
              setGeneratedTags(data.tags || []);
              setGeneratedMappings(data.mappings || []);
              setTagReviewOpen(true);
            } catch {
              showToast({ msg: "Failed to retrieve tags generation result.", type: "error", duration: 3000 });
            } finally {
              if (isMounted) setIsGeneratingTags(false);
            }
          } else if (
            tagsTask.status === ASYNC_TASK_STATUS.CANCELLED ||
            tagsTask.status === ASYNC_TASK_STATUS.FAILED
          ) {
            if (tagsTaskIdRef.current === tagsTask.id) {
              tagsTaskIdRef.current = null;
              setIsGeneratingTags(false);
              if (tagsTask.status === ASYNC_TASK_STATUS.FAILED) {
                showToast({ msg: "Tags generation failed.", type: "error", duration: 3000 });
              }
            }
          }
        }
      } catch {
        // Silently ignore polling errors to avoid disrupting the UI
      }
    };

    pollAsyncTasks();
    const intervalId = setInterval(pollAsyncTasks, 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [api, boardId, loadManifest]);

  const handlePreviewHistory = useCallback(async (historyId: number) => {
    if (!api) return;
    setIsLoadingPreview(true);
    try {
      const response = await api.getHistoryEntry(historyId);
      const data = response.data;
      const entry = history.find((e) => e.id === historyId) ?? null;
      setPreviewEntry(entry);
      setPreviewContent(data.content ?? "");
      setPreviewConfidenceScores(data.confidence_scores ?? null);
    } catch {
      showToast({ msg: "Failed to load version preview.", type: "error", duration: 3000 });
    } finally {
      setIsLoadingPreview(false);
    }
  }, [api, history]);

  const cancelGeneration = useCallback(async () => {
    const taskId = manifestTaskIdRef.current;
    if (!taskId) return;
    try {
      await asyncTaskCancel(taskId);
    } catch {
      // Even if cancel API fails, clear local loading state
    } finally {
      manifestTaskIdRef.current = null;
      setIsGenerating(false);
      loadManifest({ silent: true });
    }
  }, [loadManifest]);

  const handleGenerateTags = useCallback(async () => {
    if (!api) return;
    setIsGeneratingTags(true);
    try {
      const response = await api.generateTags();
      tagsTaskIdRef.current = response.data.id;
      if (shouldShowAsyncDialog("MARKETING_TAGS_GENERATION")) {
        (editorHandler as any).openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "MARKETING_TAGS_GENERATION" },
          { useExisting: true, by: "recordType" },
        );
      }
    } catch {
      showToast({ msg: "Failed to start tags generation.", type: "error", duration: 3000 });
      tagsTaskIdRef.current = null;
      setIsGeneratingTags(false);
    }
  }, [api, editorHandler]);

  const cancelGenerateTags = useCallback(async () => {
    const taskId = tagsTaskIdRef.current;
    if (!taskId) return;
    try {
      await asyncTaskCancel(taskId);
    } catch {
      // Even if cancel API fails, clear local loading state
    } finally {
      tagsTaskIdRef.current = null;
      setIsGeneratingTags(false);
    }
  }, []);

  const handleSaveTags = useCallback(
    async (tags: GeneratedTag[], mappings: GeneratedMapping[]) => {
      if (!project) return;
      setIsSavingTags(true);
      try {
        const tagStore = project.getCrudStore(STORE_KEYS.marketingTagStore);
        const mapStore = project.getCrudStore(STORE_KEYS.problemSolutionTagMapStore);
        const currentBoardId = selectedBoard?.record?.id;

        if (!tagStore || !currentBoardId) {
          showToast({ msg: "Unable to access tag store.", type: "error", duration: 3000 });
          return;
        }

        const newRecords = tags.map((t) => ({
          board: currentBoardId,
          label: t.label,
          type: t.type,
          tag_group: t.tag_group,
          description: t.description,
        }));

        tagStore.add(newRecords);
        await project.sync();

        if (mapStore && mappings.length > 0) {
          const allTags = tagStore.records as any[];
          const labelToId = new Map<string, number>();
          for (const rec of allTags) {
            labelToId.set(rec.label, rec.id);
          }

          const mapRecords = mappings
            .map((m) => {
              const problemId = labelToId.get(m.problem_label);
              const solutionId = labelToId.get(m.solution_label);
              if (!problemId || !solutionId) return null;
              return { board: currentBoardId, problem_tag: problemId, solution_tag: solutionId };
            })
            .filter(Boolean);

          if (mapRecords.length > 0) {
            mapStore.add(mapRecords);
            await project.sync();
          }
        }

        setTagReviewOpen(false);
        showToast({ msg: `${tags.length} tags created successfully.`, type: "success", duration: 3000 });
      } catch {
        showToast({ msg: "Failed to save tags.", type: "error", duration: 3000 });
      } finally {
        setIsSavingTags(false);
      }
    },
    [project, selectedBoard],
  );

  const value: SMMGeneratorContextValue = {
    manifestContent,
    confidenceScores,
    isLoading,
    isGenerating,
    history,
    isLoadingHistory,
    previewEntry,
    previewContent,
    previewConfidenceScores,
    isLoadingPreview,
    handlePreviewHistory,
    clearHistoryPreview,
    isGeneratingTags,
    isSavingTags,
    tagReviewOpen,
    setTagReviewOpen,
    generatedTags,
    generatedMappings,
    hasOpenAIKey,
    cancelGeneration,
    handleGenerateTags,
    cancelGenerateTags,
    handleSaveTags,
  };

  return <SMMGeneratorContext.Provider value={value}>{children}</SMMGeneratorContext.Provider>;
};

export const useSMMGeneratorContext = () => {
  const context = useContext(SMMGeneratorContext);
  if (context === undefined) {
    throw new Error("useSMMGeneratorContext must be used within a SMMGeneratorProvider");
  }
  return context;
};
